import React from 'react';
import { Button, Card, Col, Form, Row } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import {
  BIT_TYPES,
  CUTTER_TYPES,
  PostJobReport,
  PostJobReportTool,
  StepProps,
  createDefaultTool
} from '../types/types';

const Step3ToolCasing: React.FC<StepProps> = ({ reportData, onChange }) => {
  const set = (field: keyof PostJobReport, value: any) =>
    onChange({ [field]: value } as any);

  const updateTool = (
    index: number,
    field: keyof PostJobReportTool,
    value: any
  ) => {
    const updated = reportData.tools.map((t, i) =>
      i === index ? { ...t, [field]: value } : t
    );
    onChange({ tools: updated });
  };

  const addTool = () => {
    if (reportData.tools.length >= 5) return;
    onChange({ tools: [...reportData.tools, createDefaultTool()] });
  };

  const removeTool = (index: number) => {
    if (reportData.tools.length <= 1) return;
    onChange({ tools: reportData.tools.filter((_, i) => i !== index) });
  };

  const numTool = (
    index: number,
    field: keyof PostJobReportTool,
    placeholder = '0',
    step = 'any'
  ) => (
    <Form.Control
      size="sm"
      type="number"
      step={step}
      placeholder={placeholder}
      value={reportData.tools[index][field] as any}
      onChange={e =>
        updateTool(
          index,
          field,
          e.target.value === '' ? '' : Number(e.target.value)
        )
      }
    />
  );

  return (
    <div>
      {/* ── Tool Entries ─────────────────────────────────────────────────────── */}
      <Card className="pjr-section-card mb-3">
        <Card.Header className="py-2 d-flex justify-content-between align-items-center">
          <span className="fw-semibold fs-9 text-uppercase">
            Tool Information
          </span>
          <Button
            size="sm"
            variant="outline-primary"
            onClick={addTool}
            disabled={reportData.tools.length >= 5}
          >
            <FontAwesomeIcon icon={faPlus} className="me-1" />
            Add Tool
          </Button>
        </Card.Header>
        <Card.Body className="p-0">
          {reportData.tools.map((tool, idx) => (
            <div
              key={idx}
              className={idx > 0 ? 'border-top pt-3 px-3 pb-3' : 'p-3'}
            >
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="fw-bold fs-9 text-primary">
                  Tool #{idx + 1}
                </span>
                {reportData.tools.length > 1 && (
                  <Button
                    size="sm"
                    variant="outline-danger"
                    onClick={() => removeTool(idx)}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </Button>
                )}
              </div>
              <Row className="g-2">
                <Col md={3}>
                  <Form.Label className="fs-9 fw-semibold mb-1">
                    Serial Number
                  </Form.Label>
                  <Form.Control
                    size="sm"
                    placeholder="e.g. DTU-13-22-2"
                    value={tool.serialNumber}
                    onChange={e =>
                      updateTool(idx, 'serialNumber', e.target.value)
                    }
                  />
                </Col>
                <Col md={2}>
                  <Form.Label className="fs-9 fw-semibold mb-1">
                    Tool Series
                  </Form.Label>
                  {numTool(idx, 'toolSeries', '16')}
                </Col>
                <Col md={3}>
                  <Form.Label className="fs-9 fw-semibold mb-1">
                    Tool Type
                  </Form.Label>
                  <Form.Control
                    size="sm"
                    placeholder="e.g. DTU-1600"
                    value={tool.toolType}
                    onChange={e => updateTool(idx, 'toolType', e.target.value)}
                  />
                </Col>
                <Col md={2}>
                  <Form.Label className="fs-9 fw-semibold mb-1">
                    Cutter Type
                  </Form.Label>
                  <Form.Select
                    size="sm"
                    value={tool.cutterType}
                    onChange={e =>
                      updateTool(idx, 'cutterType', e.target.value)
                    }
                  >
                    {CUTTER_TYPES.map(t => (
                      <option key={t}>{t}</option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={2}>
                  <Form.Label className="fs-9 fw-semibold mb-1">
                    TFA (in²)
                  </Form.Label>
                  {numTool(idx, 'tfa', '0.00', '0.01')}
                </Col>
              </Row>
              <Row className="g-2 mt-1">
                <Col md={2}>
                  <Form.Label className="fs-9 fw-semibold mb-1">
                    # Shear Screws
                  </Form.Label>
                  {numTool(idx, 'numShearScrews', '0', '1')}
                </Col>
                <Col md={2}>
                  <Form.Label className="fs-9 fw-semibold mb-1">
                    Shear Screw Wt
                  </Form.Label>
                  {numTool(idx, 'shearScrewWtValue', '0')}
                </Col>
                <Col md={2}>
                  <Form.Label className="fs-9 fw-semibold mb-1">
                    B/N Jet
                  </Form.Label>
                  <Form.Control
                    size="sm"
                    placeholder="BLANK"
                    value={tool.bnJet}
                    onChange={e => updateTool(idx, 'bnJet', e.target.value)}
                  />
                </Col>
                <Col md={3}>
                  <Form.Label className="fs-9 fw-semibold mb-1">
                    Jets Above Cutter
                  </Form.Label>
                  <Form.Control
                    size="sm"
                    placeholder="0"
                    value={tool.jetsAboveCutter}
                    onChange={e =>
                      updateTool(idx, 'jetsAboveCutter', e.target.value)
                    }
                  />
                </Col>
                <Col md={3}>
                  <Form.Label className="fs-9 fw-semibold mb-1">
                    Jets Below Cutter
                  </Form.Label>
                  <Form.Control
                    size="sm"
                    placeholder="e.g. 10,10,10"
                    value={tool.jetsBelowCutter}
                    onChange={e =>
                      updateTool(idx, 'jetsBelowCutter', e.target.value)
                    }
                  />
                </Col>
              </Row>
            </div>
          ))}
        </Card.Body>
      </Card>

      {/* ── Bit Information ───────────────────────────────────────────────────── */}
      <Card className="pjr-section-card green mb-3">
        <Card.Header className="py-2 fw-semibold fs-9 text-uppercase">
          Bit Information
        </Card.Header>
        <Card.Body className="py-3">
          <Row className="g-3">
            <Col md={2}>
              <Form.Group>
                <Form.Label className="fs-9 fw-semibold mb-1">
                  Bit Size (in)
                </Form.Label>
                <Form.Control
                  size="sm"
                  type="number"
                  step="0.125"
                  placeholder="0"
                  value={reportData.bitSize}
                  onChange={e =>
                    set(
                      'bitSize',
                      e.target.value === '' ? '' : Number(e.target.value)
                    )
                  }
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label className="fs-9 fw-semibold mb-1">
                  Bit Manufacturer
                </Form.Label>
                <Form.Control
                  size="sm"
                  placeholder="e.g. Smith"
                  value={reportData.bitMfg}
                  onChange={e => set('bitMfg', e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label className="fs-9 fw-semibold mb-1">
                  Bit Type
                </Form.Label>
                <Form.Select
                  size="sm"
                  value={reportData.bitType}
                  onChange={e => set('bitType', e.target.value)}
                >
                  <option value="">-- Select --</option>
                  {BIT_TYPES.map(t => (
                    <option key={t}>{t}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label className="fs-9 fw-semibold mb-1">
                  Bit Jets
                </Form.Label>
                <Form.Control
                  size="sm"
                  placeholder="e.g. 24/24/24/20"
                  value={reportData.bitJets}
                  onChange={e => set('bitJets', e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label className="fs-9 fw-semibold mb-1">
                  Bit TFA (in²)
                </Form.Label>
                <Form.Control
                  size="sm"
                  type="number"
                  step="0.001"
                  placeholder="0.000"
                  value={reportData.bitTFA}
                  onChange={e =>
                    set(
                      'bitTFA',
                      e.target.value === '' ? '' : Number(e.target.value)
                    )
                  }
                />
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* ── MWD Information ───────────────────────────────────────────────────── */}
      <Card className="pjr-section-card orange mb-3">
        <Card.Header className="py-2 fw-semibold fs-9 text-uppercase">
          MWD Information
        </Card.Header>
        <Card.Body className="py-3">
          <Row className="g-3">
            <Col md={3}>
              <Form.Group>
                <Form.Label className="fs-9 fw-semibold mb-1">
                  MWD Size (in)
                </Form.Label>
                <Form.Control
                  size="sm"
                  type="number"
                  step="0.125"
                  placeholder="0"
                  value={reportData.mwdSize}
                  onChange={e =>
                    set(
                      'mwdSize',
                      e.target.value === '' ? '' : Number(e.target.value)
                    )
                  }
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label className="fs-9 fw-semibold mb-1">
                  MWD Manufacturer
                </Form.Label>
                <Form.Control
                  size="sm"
                  placeholder="e.g. Schlumberger"
                  value={reportData.mwdMfg}
                  onChange={e => set('mwdMfg', e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </div>
  );
};

export default Step3ToolCasing;
